import React, { useState, useEffect } from "react";
import { User, Calendar, FileText, Home, MapPin, Phone, Check, X, Info, Footprints, Car, Bike, CreditCard, Clock } from "lucide-react";
import { catalogos as apiCatalogos } from "../api/modulos.service";
import api from "../api/axios";
import { enDiasSV } from "../utils/fecha";
import useAuthStore from "../store/useAuthStore";
import { Button } from "./shared/Button";
import { DatePicker } from "./shared/DatePicker";

/**
 * Cómo llega el visitante.
 *
 * Son tres botones y no una lista desplegable a propósito: en la caseta esto
 * se toca con el dedo, con prisa y a veces con una mano. Una lista obliga a
 * abrir, buscar y acertar; tres botones se tocan de una.
 */
const FORMAS_DE_LLEGAR = [
  { valor: "foot", etiqueta: "A pie", icono: Footprints },
  { valor: "vehicle", etiqueta: "Vehículo", icono: Car },
  { valor: "motorcycle", etiqueta: "Moto", icono: Bike },
];

const styles = {
  form: {
    width: "100%",
  },
  inputGroup: {
    marginBottom: "var(--space-5)",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    fontSize: "var(--text-sm)",
    fontWeight: "500",
    color: "var(--text)",
    marginBottom: "var(--space-2)",
  },
  inputWrapper: {
    position: "relative",
  },
  inputIcon: {
    position: "absolute",
    left: "var(--space-3)",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-tertiary)",
  },
  input: {
    width: "100%",
    padding: "var(--space-3) var(--space-4)",
    paddingLeft: "2.5rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-base)",
    color: "var(--text)",
    transition: "all 0.2s ease",
    minHeight: "44px", // Touch-friendly size
  },
  /* Los tres botones de la forma de llegada. */
  llegada: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "var(--space-2)",
  },
  llegadaOpcion: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-2)",
    // 64 px: el dedo del guardia, no el ratón de nadie.
    minHeight: "64px",
    padding: "var(--space-3)",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    color: "var(--text-secondary)",
    fontSize: "var(--text-sm)",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  llegadaActiva: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--gold-subtle)",
    color: "var(--text)",
    boxShadow: "0 0 0 4px var(--gold-subtle)",
  },
  /* La placa se escribe en mayúsculas y separada: se dicta letra por letra. */
  placa: {
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: "600",
  },
  inputFocus: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--bg-secondary)",
    boxShadow: "0 0 0 4px var(--gold-subtle)",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: "var(--space-3) var(--space-4)",
    paddingLeft: "2.5rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-base)",
    color: "var(--text)",
    transition: "all 0.2s ease",
    resize: "vertical",
    minHeight: "100px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "var(--space-4)",
  },
  message: {
    padding: "var(--space-3) var(--space-4)",
    borderRadius: "var(--radius-lg)",
    fontSize: "var(--text-sm)",
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    marginBottom: "var(--space-4)",
  },
  successMessage: {
    backgroundColor: "var(--success-bg)",
    color: "var(--success-text)",
  },
  errorMessage: {
    backgroundColor: "var(--danger-bg)",
    color: "var(--danger-text)",
  },
  actions: {
    display: "flex",
    gap: "var(--space-3)",
    justifyContent: "flex-end",
    marginTop: "var(--space-6)",
  },
  // Responsive styles
  '@media (max-width: 768px)': {
    row: {
      gridTemplateColumns: "1fr",
      gap: "var(--space-4)",
    },
    actions: {
      flexDirection: "column-reverse",
      gap: "var(--space-3)",
    },
    input: {
      fontSize: "16px", // Prevent zoom on iOS
    },
    textarea: {
      fontSize: "16px", // Prevent zoom on iOS
    },
  },
};

export default function VisitForm({ onVisitCreated }) {
  const { user } = useAuthStore();
  const initialForm = {
    visitorName: "",
    date: "",
    note: "",
    visitorPhone: "",
    /* `arrivalMode` estaba faltando aquí y se usaba en el resto del
       componente: al abrir el formulario, ninguno de los tres botones salía
       marcado y `plate` era `undefined`. Funcionaba —el servidor pone «a
       pie» por defecto— pero se veía roto. */
    arrivalMode: "foot",
    plate: "",
    vehicleBrand: "",
    vehicleColor: "",
    visitorType: "invitado",
    passType: "hoy",
    instructions: "",
    houseNumber: user?.role === "guard" ? "" : user?.houseNumber,
    polygon: user?.role === "guard" ? "" : user?.polygon,
  };

  const [form, setForm] = useState(initialForm);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  /* Los tipos de visitante y las duraciones permitidas salen del servidor. Si
     la llamada falla, el formulario sigue con los valores por defecto: una
     lista de opciones no puede impedir registrar una visita. */
  const [catalogos, setCatalogos] = useState(null);

  useEffect(() => {
    apiCatalogos().then(setCatalogos).catch(() => setCatalogos(null));
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Mañana EN EL SALVADOR. Calculado en UTC, después de las 6 de la tarde
     esto daba «pasado mañana», así que el residente no podía crear una
     visita para el día siguiente justo cuando más gente lo hace: de noche. */
  const minDate = enDiasSV(1);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /**
   * Cambia la forma de llegada y, si pasa a «a pie», borra la placa.
   *
   * Si sólo se escondiera el campo dejando el valor puesto, se mandaría una
   * placa para una visita peatonal. El servidor la rechaza —y con razón, sería
   * un dato que nadie apuntó—, así que el residente vería un error por algo
   * que ya ni siquiera ve en pantalla.
   */
  const elegirLlegada = (valor) =>
    setForm((actual) => ({
      ...actual,
      arrivalMode: valor,
      plate: valor === "foot" ? "" : actual.plate,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      // Simply send the date as a string in YYYY-MM-DD format
      // The backend will handle timezone conversion
      const payload = {
        visitorName: form.visitorName,
        date: form.date, // Send as YYYY-MM-DD string
        note: form.note,
        visitorPhone: form.visitorPhone,
        visitorType: form.visitorType,
        passType: form.passType,
        instructions: form.instructions,
        arrivalMode: form.arrivalMode,
        /* Sin placa si viene a pie: el servidor rechaza una placa suelta, y
           con razón —sería un dato que nadie apuntó—. */
        plate: form.arrivalMode === "foot" ? "" : form.plate,
        vehicleBrand: form.arrivalMode === "foot" ? "" : form.vehicleBrand,
        vehicleColor: form.arrivalMode === "foot" ? "" : form.vehicleColor,
      };

      if (user.role === "guard") {
        payload.houseNumber = form.houseNumber;
        payload.polygon = form.polygon;
      }

      const response = await api.post("/visits", payload);
      const createdVisit = response.data.visit || response.data;
      
      let successMessage = "Visita registrada correctamente";
      if (response.data.qrGenerated && response.data.smsSent) {
        successMessage += ". Se envió el código QR al visitante por SMS.";
      } else if (response.data.qrGenerated && !response.data.smsSent) {
        successMessage += ". QR generado pero no se pudo enviar SMS.";
      }
      
      setMessage({ type: "success", text: successMessage });

      // Se reusa `initialForm` en vez de repetir la lista de campos: la copia
      // suelta que había aquí ya se quedaba atrás cada vez que se agregaba uno.
      setForm(initialForm);

      if (onVisitCreated) {
        setTimeout(() => {
          onVisitCreated(createdVisit);
        }, 1000);
      }
    } catch (err) {
      setMessage({ type: "error", text: "Error al registrar la visita" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setMessage(null);
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {message && (
        <div
          style={{
            ...styles.message,
            ...(message.type === "success"
              ? styles.successMessage
              : styles.errorMessage),
          }}
        >
          {message.type === "success" ? <Check size={16} /> : <X size={16} />}
          {message.text}
        </div>
      )}

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <User size={16} />
          Nombre del visitante
        </label>
        <div style={styles.inputWrapper}>
          <User size={18} style={styles.inputIcon} />
          <input
            name="visitorName"
            placeholder="Ingrese el nombre completo"
            value={form.visitorName}
            onChange={handleChange}
            onFocus={() => setFocusedField("visitorName")}
            onBlur={() => setFocusedField(null)}
            required
            style={{
              ...styles.input,
              ...(focusedField === "visitorName" ? styles.inputFocus : {}),
              ...(isMobile ? { fontSize: '16px' } : {}),
            }}
          />
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <Calendar size={16} />
          Fecha de visita
        </label>
        <DatePicker
          value={form.date}
          onChange={(date) => handleChange({ target: { name: 'date', value: date } })}
          placeholder="Seleccionar fecha"
          minDate={new Date()}
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <Phone size={16} />
          Teléfono del visitante
        </label>
        <div style={styles.inputWrapper}>
          <Phone size={18} style={styles.inputIcon} />
          <input
            name="visitorPhone"
            placeholder="7000-0000 (opcional - para enviar SMS)"
            value={form.visitorPhone}
            onChange={handleChange}
            onFocus={() => setFocusedField("visitorPhone")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...styles.input,
              ...(focusedField === "visitorPhone" ? styles.inputFocus : {}),
              ...(isMobile ? { fontSize: '16px' } : {}),
            }}
          />
        </div>
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Info size={12} />
          Si proporciona el teléfono, el visitante recibirá el enlace por SMS
        </div>
      </div>

      {/* Qué clase de visita es y cuánto vale el pase. Los dos salen del
          catálogo del servidor: ofrecer una duración que la residencial no
          permite sería una trampa para el residente. */}
      <div style={styles.row}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            <User size={16} />
            Tipo de visitante
          </label>
          <select
            name="visitorType"
            value={form.visitorType}
            onChange={handleChange}
            style={{ ...styles.input, paddingLeft: "var(--space-3)", ...(isMobile ? { fontSize: "16px" } : {}) }}
          >
            {(catalogos?.tiposVisitante || [{ valor: "invitado", etiqueta: "Invitado" }]).map((t) => (
              <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
            ))}
          </select>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            <Clock size={16} />
            El pase vale
          </label>
          <select
            name="passType"
            value={form.passType}
            onChange={handleChange}
            style={{ ...styles.input, paddingLeft: "var(--space-3)", ...(isMobile ? { fontSize: "16px" } : {}) }}
          >
            {(catalogos?.tiposPase || [{ valor: "hoy", etiqueta: "Sólo hoy" }]).map((t) => (
              <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <Car size={16} />
          ¿Cómo llega?
        </label>
        <div style={styles.llegada} role="radiogroup" aria-label="Forma de llegada">
          {FORMAS_DE_LLEGAR.map(({ valor, etiqueta, icono: Icono }) => {
            const elegida = form.arrivalMode === valor;
            return (
              <button
                key={valor}
                type="button"
                role="radio"
                aria-checked={elegida}
                onClick={() => elegirLlegada(valor)}
                style={{
                  ...styles.llegadaOpcion,
                  ...(elegida ? styles.llegadaActiva : {}),
                }}
              >
                <Icono size={20} />
                {etiqueta}
              </button>
            );
          })}
        </div>
      </div>

      {/* La placa aparece sólo si hace falta. Pedirla siempre y dejarla vacía
          en las visitas a pie llenaría el registro de campos en blanco que
          después nadie sabe si se olvidaron o no aplicaban. */}
      {form.arrivalMode !== "foot" && (
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            <CreditCard size={16} />
            Placa {form.arrivalMode === "motorcycle" ? "de la moto" : "del vehículo"}
          </label>
          <div style={styles.inputWrapper}>
            <CreditCard size={18} style={styles.inputIcon} />
            <input
              name="plate"
              placeholder={form.arrivalMode === "motorcycle" ? "M12-345" : "P123-456"}
              value={form.plate}
              onChange={handleChange}
              onFocus={() => setFocusedField("plate")}
              onBlur={() => setFocusedField(null)}
              maxLength={16}
              // El teclado del teléfono abre en mayúsculas y sin corrector:
              // una placa no es una palabra y el corrector la destroza.
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{
                ...styles.input,
                ...styles.placa,
                ...(focusedField === "plate" ? styles.inputFocus : {}),
                ...(isMobile ? { fontSize: "16px" } : {}),
              }}
            />
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Info size={12} />
            Es lo que el guardia va a comparar en la caseta
          </div>
        </div>
      )}

      {/* Lo que el visitante y el guardia van a leer al llegar. */}
      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <Info size={16} />
          Instrucciones para la caseta
        </label>
        <div style={styles.inputWrapper}>
          <Info size={18} style={styles.inputIcon} />
          <input
            name="instructions"
            placeholder="Opcional. Por ejemplo: que se estacione frente a la casa"
            value={form.instructions}
            onChange={handleChange}
            style={{ ...styles.input, ...(isMobile ? { fontSize: '16px' } : {}) }}
          />
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          <FileText size={16} />
          Motivo de la visita
        </label>
        <div style={styles.inputWrapper}>
          <FileText size={18} style={styles.inputIcon} />
          <textarea
            name="note"
            placeholder="Describa el motivo de la visita (opcional)"
            value={form.note}
            onChange={handleChange}
            onFocus={() => setFocusedField("note")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...styles.textarea,
              ...(focusedField === "note" ? styles.inputFocus : {}),
              ...(isMobile ? { fontSize: '16px' } : {}),
            }}
          />
        </div>
      </div>

      {user.role === "guard" && (
        <div style={{
          ...styles.row,
          ...(isMobile ? { gridTemplateColumns: '1fr', gap: 'var(--space-4)' } : {})
        }}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <MapPin size={16} />
              Polígono
            </label>
            <div style={styles.inputWrapper}>
              <MapPin size={18} style={styles.inputIcon} />
              <input
                name="polygon"
                placeholder="Ej: A, B, C"
                value={form.polygon}
                onChange={handleChange}
                onFocus={() => setFocusedField("polygon")}
                onBlur={() => setFocusedField(null)}
                required
                style={{
                  ...styles.input,
                  ...(focusedField === "polygon" ? styles.inputFocus : {}),
                  ...(isMobile ? { fontSize: '16px' } : {}),
                }}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <Home size={16} />
              Número de casa
            </label>
            <div style={styles.inputWrapper}>
              <Home size={18} style={styles.inputIcon} />
              <input
                name="houseNumber"
                placeholder="Ej: 15, 23A"
                value={form.houseNumber}
                onChange={handleChange}
                onFocus={() => setFocusedField("houseNumber")}
                onBlur={() => setFocusedField(null)}
                required
                style={{
                  ...styles.input,
                  ...(focusedField === "houseNumber" ? styles.inputFocus : {}),
                  ...(isMobile ? { fontSize: '16px' } : {}),
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div style={{
        ...styles.actions,
        ...(isMobile ? { flexDirection: 'column-reverse', gap: 'var(--space-3)' } : {})
      }}>
        <Button
          type="button"
          variant="secondary"
          onClick={handleReset}
          disabled={loading}
          style={{
            ...(isMobile ? { width: '100%' } : {})
          }}
        >
          Limpiar
        </Button>
        <Button
          type="submit"
          loading={loading}
          disabled={loading}
          style={{
            color: "white",
            ...(isMobile ? { width: '100%' } : {})
          }}
        >
          Registrar Visita
        </Button>
      </div>
    </form>
  );
}
